import { radius, spacing, ThemeColors, useColors } from "@/constants/theme";
import { useAppStore } from "@/stores/useStore";
import { TeacherTab } from "@/types";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import { Plus } from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import { Alert, FlatList, Pressable, StyleSheet, View } from "react-native";
import { Button, Card, Text, TextInput } from "react-native-paper";

const createClientId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function TeacherScreen() {
  const {
    classes,
    currentUser,
    loadTeachingContext,
    loadRemoteData,
    posts,
    cards,
    subjects,
    getProfileById,
    schools,
    teacherAssignments,
    teacherTabs,
    upsertTeacherTab,
  } = useAppStore();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const canUseTeacherTools =
    currentUser?.role === "teacher" || currentUser?.role === "admin";

  useEffect(() => {
    loadTeachingContext().catch((error) => {
      console.warn("Failed to load teacher tools", error);
    });
    loadRemoteData().catch((error) => {
      console.warn("Failed to load teacher dashboard data", error);
    });
  }, [loadTeachingContext, loadRemoteData]);

  // 生徒の学習状況ダッシュボード（クラス・学校・公開の発見を対象）
  const dashboard = useMemo(() => {
    const studentPosts = posts.filter(
      (post) =>
        post.status !== "deleted" &&
        post.user_id !== currentUser?.id &&
        post.visibility !== "private",
    );
    const contributorIds = new Set(studentPosts.map((post) => post.user_id));

    const subjectCounts: Record<string, number> = {};
    studentPosts.forEach((post) => {
      post.card_ids.forEach((cardId) => {
        const card = cards.find((c) => c.id === cardId);
        if (!card) return;
        subjectCounts[card.subject_id] =
          (subjectCounts[card.subject_id] || 0) + 1;
      });
    });
    const topSubjects = Object.entries(subjectCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);

    const now = Date.now();
    const weeklyPosts = studentPosts.filter(
      (post) =>
        now - new Date(post.created_at).getTime() < 7 * 24 * 60 * 60 * 1000,
    ).length;

    const recent = [...studentPosts]
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, 5);

    return {
      totalDiscoveries: studentPosts.length,
      contributors: contributorIds.size,
      weeklyPosts,
      topSubjects,
      recent,
    };
  }, [posts, cards, currentUser?.id]);

  const defaultAssignment = teacherAssignments[0];
  const assignmentLabel = useMemo(() => {
    if (!defaultAssignment) return "担当範囲はまだ設定されていません";

    if (defaultAssignment.scope_type === "school") {
      return schools.find((school) => school.id === defaultAssignment.school_id)?.name ?? "学校";
    }

    if (defaultAssignment.scope_type === "class") {
      return classes.find((item) => item.id === defaultAssignment.class_id)?.name ?? "クラス";
    }

    return "個別生徒";
  }, [classes, defaultAssignment, schools]);

  const handleAddTab = async () => {
    const tabTitle = title.trim();
    if (!tabTitle || !currentUser || !defaultAssignment) return;

    const scopeId =
      defaultAssignment.school_id ??
      defaultAssignment.class_id ??
      defaultAssignment.student_id;

    if (!scopeId) return;

    const tab: TeacherTab = {
      id: createClientId("teacher-tab"),
      teacher_id: currentUser.id,
      title: tabTitle,
      scope_type: defaultAssignment.scope_type,
      scope_id: scopeId,
      sort_order: teacherTabs.length + 1,
      is_active: true,
    };

    setIsSaving(true);
    try {
      await upsertTeacherTab(tab);
      setTitle("");
    } catch (error) {
      console.warn("Failed to add teacher tab", error);
      Alert.alert("追加できませんでした", "担当範囲と通信状態を確認してください。");
    } finally {
      setIsSaving(false);
    }
  };

  if (!canUseTeacherTools) {
    return (
      <View style={styles.centered}>
        <Text variant="titleMedium" style={styles.title}>
          先生用機能
        </Text>
        <Text style={styles.muted}>
          担当クラスや公開範囲の管理は、先生・管理者アカウントで利用できます。
        </Text>
      </View>
    );
  }

  return (
    <FlatList
      style={styles.container}
      data={teacherTabs}
      keyExtractor={(item) => item.id}
      contentContainerStyle={styles.list}
      ListHeaderComponent={
        <View style={styles.headerWrap}>
          <View style={styles.header}>
            <Text variant="headlineSmall" style={styles.title}>
              先生用管理
            </Text>
            <Text style={styles.muted}>担当範囲: {assignmentLabel}</Text>
          </View>

          {/* 学習状況ダッシュボード */}
          <Card style={styles.dashCard} mode="elevated">
            <Card.Title title="📊 クラスの学習状況" />
            <Card.Content>
              <View style={styles.statRow}>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>
                    {dashboard.totalDiscoveries}
                  </Text>
                  <Text style={styles.statLabel}>発見の総数</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{dashboard.contributors}</Text>
                  <Text style={styles.statLabel}>参加した生徒</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{dashboard.weeklyPosts}</Text>
                  <Text style={styles.statLabel}>今週の投稿</Text>
                </View>
              </View>

              {dashboard.topSubjects.length > 0 && (
                <View style={styles.subjectBlock}>
                  <Text style={styles.blockLabel}>よく使われた科目</Text>
                  {dashboard.topSubjects.map(([subjectId, count]) => {
                    const subject = subjects.find((s) => s.id === subjectId);
                    const max = dashboard.topSubjects[0][1] || 1;
                    return (
                      <View key={subjectId} style={styles.subjectRow}>
                        <Text style={styles.subjectName}>
                          {subject?.name ?? "その他"}
                        </Text>
                        <View style={styles.barTrack}>
                          <View
                            style={[
                              styles.barFill,
                              {
                                width: `${(count / max) * 100}%`,
                                backgroundColor:
                                  subject?.color_code ?? c.primary,
                              },
                            ]}
                          />
                        </View>
                        <Text style={styles.subjectCount}>{count}</Text>
                      </View>
                    );
                  })}
                </View>
              )}
            </Card.Content>
          </Card>

          {/* 最近の発見 */}
          {dashboard.recent.length > 0 && (
            <Card style={styles.dashCard} mode="elevated">
              <Card.Title title="🕒 最近の発見" />
              <Card.Content>
                {dashboard.recent.map((post) => {
                  const author = getProfileById(post.user_id);
                  return (
                    <View key={post.id} style={styles.recentItem}>
                      <Text style={styles.recentAuthor}>
                        {author?.display_name ?? "生徒"}・
                        {formatDistanceToNow(new Date(post.created_at), {
                          addSuffix: true,
                          locale: ja,
                        })}
                      </Text>
                      <Text style={styles.recentBody} numberOfLines={2}>
                        {post.body}
                      </Text>
                    </View>
                  );
                })}
              </Card.Content>
            </Card>
          )}

          <View style={styles.form}>
            <Text style={styles.blockLabel}>先生用タブを追加</Text>
            <TextInput
              label="新しいタブ名"
              value={title}
              onChangeText={setTitle}
              mode="outlined"
            />
            <Button
              mode="contained"
              icon={() => <Plus size={16} color="#fff" />}
              disabled={!title.trim() || !defaultAssignment || isSaving}
              loading={isSaving}
              onPress={handleAddTab}
            >
              タブを追加
            </Button>
          </View>
        </View>
      }
      ListEmptyComponent={
        <Text style={styles.muted}>
          まだ先生用タブはありません。管理サイトで担当範囲を設定後、ここから追加できます。
        </Text>
      }
      renderItem={({ item }) => (
        <Pressable style={styles.row}>
          <Text variant="titleMedium" style={styles.rowTitle}>
            {item.title}
          </Text>
          <Text style={styles.muted}>
            {item.scope_type} / {item.scope_id}
          </Text>
        </Pressable>
      )}
    />
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  centered: {
    alignItems: "center",
    flex: 1,
    gap: 12,
    justifyContent: "center",
    padding: 24,
  },
  container: {
    backgroundColor: c.bg,
    flex: 1,
  },
  headerWrap: {
    marginBottom: spacing.md,
  },
  form: {
    backgroundColor: c.surface,
    gap: 12,
    padding: 16,
    borderRadius: radius.md,
    marginTop: spacing.md,
  },
  header: {
    backgroundColor: c.surface,
    borderBottomColor: c.border,
    borderBottomWidth: 1,
    gap: 4,
    padding: 20,
    marginHorizontal: -16,
    marginTop: -16,
    marginBottom: spacing.md,
  },
  dashCard: {
    backgroundColor: c.surface,
    marginBottom: spacing.md,
  },
  statRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    paddingVertical: spacing.sm,
  },
  statItem: {
    alignItems: "center",
  },
  statValue: {
    fontSize: 26,
    fontWeight: "700",
    color: c.primary,
  },
  statLabel: {
    fontSize: 12,
    color: c.textSecondary,
    marginTop: 2,
  },
  subjectBlock: {
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  blockLabel: {
    fontWeight: "700",
    color: c.textSecondary,
    fontSize: 13,
    marginBottom: 2,
  },
  subjectRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  subjectName: {
    width: 56,
    fontSize: 12,
    fontWeight: "700",
    color: c.textPrimary,
  },
  barTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: c.surfaceAlt,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 4,
  },
  subjectCount: {
    width: 28,
    textAlign: "right",
    fontSize: 12,
    color: c.textSecondary,
    fontWeight: "700",
  },
  recentItem: {
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
  },
  recentAuthor: {
    fontSize: 11,
    fontWeight: "700",
    color: c.textMuted,
    marginBottom: 2,
  },
  recentBody: {
    fontSize: 13,
    color: c.textPrimary,
    lineHeight: 18,
  },
  list: {
    gap: 10,
    padding: 16,
  },
  muted: {
    color: c.textSecondary,
  },
  row: {
    backgroundColor: c.surface,
    borderColor: c.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: 4,
    padding: 14,
  },
  rowTitle: {
    fontWeight: "700",
    color: c.textPrimary,
  },
  title: {
    fontWeight: "700",
    color: c.textPrimary,
  },
});
